import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Alert,
  Pagination
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { formatDate as formatDateUtil } from '../utils/format';
import { ensureLogin, fetchProjects, fetchReports, createReport, downloadReport, deleteReport } from '../utils/api';

// Removido: projetos simulados. Vamos carregar projetos reais.

const mockReportTemplates = [
  { 
    id: 1, 
    title: 'Manual de Projeto', 
    description: 'Documento completo com todas as informações do projeto, incluindo objetivos, âmbito, cronograma, equipa e orçamento.',
    type: 'project_manual',
    icon: <DescriptionIcon />,
    availablePhases: ['planning'],
    requiredFields: ['objectives', 'scope', 'team', 'budget', 'timeline']
  },
  { 
    id: 2, 
    title: 'Ficha de Projeto', 
    description: 'Resumo das informações essenciais do projeto em formato de ficha.',
    type: 'project_sheet',
    icon: <AssignmentIcon />,
    availablePhases: ['planning', 'execution', 'closing'],
    requiredFields: ['title', 'code', 'client', 'startDate', 'endDate']
  },
  { 
    id: 3, 
    title: 'Matriz de Risco', 
    description: 'Análise detalhada dos riscos do projeto, incluindo probabilidade, impacto e estratégias de mitigação.',
    type: 'risk_matrix',
    icon: <AssessmentIcon />,
    availablePhases: ['planning', 'execution'],
    requiredFields: ['risks']
  },
  { 
    id: 4, 
    title: 'Relatório Intermédio', 
    description: 'Relatório de progresso do projeto, incluindo atividades realizadas, próximos passos e indicadores de desempenho.',
    type: 'intermediate_report',
    icon: <AssessmentIcon />,
    availablePhases: ['execution'],
    requiredFields: ['activities', 'indicators', 'nextSteps']
  },
  { 
    id: 5, 
    title: 'Relatório Final', 
    description: 'Relatório completo de encerramento do projeto, incluindo resultados, lições aprendidas e recomendações.',
    type: 'final_report',
    icon: <AssessmentIcon />,
    availablePhases: ['closing'],
    requiredFields: ['results', 'lessonsLearned', 'recommendations']
  },
  { 
    id: 6, 
    title: 'Relatório de Impacto', 
    description: 'Análise do impacto do projeto nos objetivos de negócio do cliente e nos participantes.',
    type: 'impact_report',
    icon: <AssessmentIcon />,
    availablePhases: ['closing'],
    requiredFields: ['impact', 'testimonials', 'metrics']
  },
  {
    id: 7,
    title: 'Relatório de Avaliações',
    description: 'Compilação dos resultados das avaliações do projeto: médias, distribuições e estatísticas básicas.',
    type: 'evaluation_summary',
    icon: <AssessmentIcon />,
    availablePhases: ['execution', 'closing'],
    requiredFields: ['evaluations', 'metrics']
  },
];

// Removido: relatórios simulados. Usaremos API real.

const Reports = () => {
  const [templates, setTemplates] = useState([]);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [generationSuccess, setGenerationSuccess] = useState(false);
  const [projects, setProjects] = useState([]);
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [shareTargetReport, setShareTargetReport] = useState(null);
  const [shareEmails, setShareEmails] = useState('');
  const [shareSuccess, setShareSuccess] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const load = async () => {
      await ensureLogin();
      setTemplates(mockReportTemplates);
      const [proj, reps] = await Promise.all([fetchProjects(), fetchReports()]);
      setProjects(proj);
      // enriquecer com projectTitle se possível
      const enriched = reps.map(r => ({
        ...r,
        projectTitle: proj.find(p => p.id === (r.projectId || r.project_id))?.title || ''
      }));
      setGeneratedReports(enriched);
      setFilteredReports(enriched);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    // Aplicar filtros
    let result = [...generatedReports];
    
    if (projectFilter) {
      result = result.filter(report => report.projectId === parseInt(projectFilter));
    }
    
    if (typeFilter) {
      result = result.filter(report => report.type === typeFilter);
    }
    
    setFilteredReports(result);
  }, [generatedReports, projectFilter, typeFilter]);

  useEffect(() => {
    // Reset página ao alterar filtros/conteúdo
    setPage(1);
  }, [filteredReports]);

  const handleOpenDialog = async (template) => {
    setSelectedTemplate(template);
    setSelectedProject('');
    // Garantir que projetos estão carregados quando abrir o diálogo
    if (!projects || projects.length === 0) {
      await ensureLogin();
      try {
        const proj = await fetchProjects();
        setProjects(proj);
      } catch (e) {
        console.error('Falha ao carregar projetos', e);
      }
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTemplate(null);
    setSelectedProject('');
  };

  const handleGenerateReport = async () => {
    await ensureLogin();
    const projectId = parseInt(selectedProject);
    const selectedProjectData = projects.find(p => p.id === projectId);
    if (selectedProjectData && selectedTemplate) {
      await createReport({
        title: `${selectedTemplate.title} - ${selectedProjectData.title}`,
        type: selectedTemplate.type,
        projectId
      });
      const reps = await fetchReports();
      const enriched = reps.map(r => ({
        ...r,
        projectTitle: projects.find(p => p.id === (r.projectId || r.project_id))?.title || ''
      }));
      setGeneratedReports(enriched);
      setFilteredReports(enriched);
      setGenerationSuccess(true);
      setTimeout(() => setGenerationSuccess(false), 3000);
      handleCloseDialog();
    }
  };

  const handleDownload = async (report) => {
    await ensureLogin();
    try {
      const blob = await downloadReport(report.id);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const safeName = (report.title || 'Documento').replace(/[^a-z0-9\-\_]+/gi,'_');
      a.download = `${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Falha ao baixar documento');
    }
  };

  const handleDelete = async (report) => {
    const confirmed = window.confirm(`Deseja apagar o documento "${report.title}"?`);
    if (!confirmed) return;
    try {
      await ensureLogin();
      await deleteReport(report.id);
      const reps = await fetchReports();
      const enriched = reps.map(r => ({
        ...r,
        projectTitle: projects.find(p => p.id === (r.projectId || r.project_id))?.title || ''
      }));
      setGeneratedReports(enriched);
      setFilteredReports(enriched);
    } catch (err) {
      console.error(err);
      alert('Falha ao apagar documento');
    }
  };

  // Removido fluxo de partilha por email

  const getReportTypeIcon = (type) => {
    switch (type) {
      case 'project_manual': return <DescriptionIcon />;
      case 'project_sheet': return <AssignmentIcon />;
      case 'risk_matrix': 
      case 'intermediate_report':
      case 'final_report':
      case 'impact_report':
      case 'evaluation_summary':
        return <AssessmentIcon />;
      default: return <DescriptionIcon />;
    }
  };

  const getReportTypeName = (type) => {
    switch (type) {
      case 'project_manual': return 'Manual de Projeto';
      case 'project_sheet': return 'Ficha de Projeto';
      case 'risk_matrix': return 'Matriz de Risco';
      case 'intermediate_report': return 'Relatório Intermédio';
      case 'final_report': return 'Relatório Final';
      case 'impact_report': return 'Relatório de Impacto';
      case 'evaluation_summary': return 'Relatório de Avaliações';
      default: return 'Documento';
    }
  };

  const formatDateStr = (dateString) => {
    if (!dateString) return '';
    return formatDateUtil(dateString);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>A carregar relatórios…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      <Typography variant="h4" component="div" gutterBottom>
        Relatórios e Documentos
      </Typography>
      
      {generationSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Relatório gerado com sucesso!
        </Alert>
      )}
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtros
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Projeto</InputLabel>
              <Select
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                label="Projeto"
              >
                <MenuItem value="">Todos</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Tipo de Documento</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Tipo de Documento"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="project_manual">Manual de Projeto</MenuItem>
                <MenuItem value="project_sheet">Ficha de Projeto</MenuItem>
                <MenuItem value="risk_matrix">Matriz de Risco</MenuItem>
                <MenuItem value="intermediate_report">Relatório Intermédio</MenuItem>
                <MenuItem value="final_report">Relatório Final</MenuItem>
                <MenuItem value="impact_report">Relatório de Impacto</MenuItem>
                <MenuItem value="evaluation_summary">Relatório de Avaliações</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Typography variant="h6" gutterBottom>
            Modelos de Documentos
          </Typography>
          <Paper sx={{ p: 2 }}>
            <List>
              {templates.map((template) => (
                <React.Fragment key={template.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      {template.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={template.title} 
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="text.secondary">{template.description}</Typography>
                          <Button 
                            variant="outlined" 
                            size="small"
                            sx={{ mt: 1 }}
                            onClick={() => handleOpenDialog(template)}
                          >
                            Gerar
                          </Button>
                        </Box>
                      }
                    />
                  </ListItem>
                  {template.id < templates.length && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Documentos Gerados
            </Typography>
          </Box>
          
          {filteredReports.length > 0 ? (
            <Paper sx={{ p: 0 }}>
              <List>
                {filteredReports
                  .slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize)
                  .map((report, idx) => (
                  <React.Fragment key={report.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemIcon>
                        {getReportTypeIcon(report.type)}
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {report.title}
                            </Typography>
                            <Box>
                              <Button 
                                startIcon={<DownloadIcon />} 
                                size="small"
                                onClick={() => handleDownload(report)}
                                sx={{ mr: 1 }}
                              >
                                Download
                              </Button>
                              <Button 
                                startIcon={<DeleteIcon />} 
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() => handleDelete(report)}
                              >
                                Apagar
                              </Button>
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">Projeto: {report.projectTitle || '—'}</Typography>
                            <Typography variant="body2" color="text.secondary">Criado em: {formatDateStr(report.createdAt)}</Typography>
                            <Typography variant="body2" color="text.secondary">Criado por: {report.createdBy || '—'}</Typography>
                            <Box sx={{ mt: 0.5 }}>
                              <Chip 
                                label={`${(report.format || 'PDF')}${report.fileSize ? ` · ${report.fileSize}` : ''}`} 
                                size="small" 
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {idx < Math.min(pageSize, filteredReports.length - (page - 1) * pageSize) - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1">
                Nenhum documento encontrado com os filtros selecionados.
              </Typography>
            </Paper>
          )}
          {filteredReports.length > 0 && (
            <Box sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: { xs: 2, md: 4 },
              py: 1,
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              zIndex: 1000
            }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Itens por página</InputLabel>
                <Select
                  label="Itens por página"
                  value={pageSize}
                  onChange={(e) => setPageSize(parseInt(e.target.value))}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
              <Pagination 
                count={Math.max(1, Math.ceil(filteredReports.length / pageSize))}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Grid>
      </Grid>
      
      {/* Diálogo para gerar relatório */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Gerar {selectedTemplate?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {selectedTemplate && (
              <>
                <Typography variant="body1" paragraph>
                  {selectedTemplate.description}
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom>
                  Fases disponíveis:
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {selectedTemplate.availablePhases.map((phase, index) => (
                    <Chip 
                      key={index}
                      label={
              phase === 'planning' ? 'Planeamento' :
                        phase === 'kickoff' ? 'Kick-off' : 
                        phase === 'execution' ? 'Execução' : 'Encerramento'
                      } 
                      size="small" 
                      color="primary"
                      variant="outlined"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
                
                <Typography variant="subtitle2" gutterBottom>
                  Campos necessários:
                </Typography>
                <Box sx={{ mb: 3 }}>
                  {selectedTemplate.requiredFields.map((field, index) => (
                    <Chip 
                      key={index}
                      label={field} 
                      size="small" 
                      color="secondary"
                      variant="outlined"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
                
                <FormControl fullWidth sx={{ mt: 2 }} required>
                  <InputLabel>Selecione o Projeto</InputLabel>
                  <Select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    label="Selecione o Projeto"
                  >
                    {projects
                      .filter(project => {
                        if (!selectedTemplate) return false;
                        // Para relatório de avaliações, permitir todos os projetos (independente da fase)
                        if (selectedTemplate.type === 'evaluation_summary') {
                          return true;
                        }
                        // Para outros templates, filtrar por fases configuradas
                        const phases = selectedTemplate.availablePhases || [];
                        if (phases.includes('planning') && project.status === 'planning') return true;
                        if (phases.includes('kickoff') && project.status === 'kickoff') return true;
                        if (phases.includes('execution') && project.status === 'execution') return true;
                        if (phases.includes('closing') && project.status === 'closing') return true;
                        return false;
                      })
                      .map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.title} ({project.code})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleGenerateReport} 
            variant="contained" 
            color="primary"
            disabled={!selectedProject}
          >
            Gerar Documento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Removido diálogo de partilha por email */}
    </Box>
  );
};

export default Reports;